<!--
  /Users/xingyunx/workplace/ws/RingcentralAnalysisPortal/src/components/ui/mobile-tooltip-badge.vue
  Mobile-friendly tooltip badge component that works on touch devices
  Combines Badge with Popover for mobile and Tooltip for desktop
  RELEVANT FILES: CallAnalysisManifest.vue, Badge.vue, Popover.vue, Tooltip.vue
-->
<script setup lang="ts">
import { ref } from 'vue';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useHover } from '@/composables/useHover';
import * as LucideIcons from 'lucide-vue-next';

interface Props {
  variant?: 'default' | 'secondary' | 'destructive' | 'outline';
  size?: 'default' | 'large';
  content: string;
  tooltipText?: string;
  showHelpIcon?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'secondary',
  size: 'default',
  showHelpIcon: true,
});

// State for popover (mobile)
const isPopoverOpen = ref(false);

// Use composable for reactive hover capability detection
const { hasHover } = useHover();

function getIconComponent(iconName: string) {
  return (LucideIcons as any)[iconName] || null;
}
</script>

<template>
  <div>
    <!-- Desktop/Hover-capable devices: Use Tooltip with hover -->
    <TooltipProvider
      v-if="hasHover && props.tooltipText"
      :delay-duration="300"
      :skip-delay-duration="100"
    >
      <Tooltip>
        <TooltipTrigger as-child>
          <Badge
            :variant="props.variant"
            class="cursor-pointer hover:opacity-80 transition-opacity flex items-center gap-1"
            :class="[props.size === 'large' && 'text-base px-3 py-1']"
          >
            {{ props.content }}
            <component
              v-if="props.showHelpIcon"
              :is="getIconComponent('HelpCircle')"
              class="h-3 w-3 opacity-70"
            />
          </Badge>
        </TooltipTrigger>
        <TooltipContent class="max-w-[350px]">
          <p>{{ props.tooltipText }}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>

    <!-- Touch-only devices: Use Popover with click -->
    <Popover v-else-if="!hasHover && props.tooltipText" v-model:open="isPopoverOpen">
      <PopoverTrigger as-child>
        <Badge
          :variant="props.variant"
          class="cursor-pointer active:opacity-70 transition-opacity flex items-center gap-1"
          :class="[props.size === 'large' && 'text-base px-3 py-1']"
        >
          {{ props.content }}
          <component
            v-if="props.showHelpIcon"
            :is="getIconComponent('HelpCircle')"
            class="h-3 w-3 opacity-70"
          />
        </Badge>
      </PopoverTrigger>
      <PopoverContent class="w-80 p-3" side="top" :side-offset="4">
        <p class="text-sm">{{ props.tooltipText }}</p>
      </PopoverContent>
    </Popover>

    <!-- No tooltip: Just badge -->
    <Badge
      v-else
      :variant="props.variant"
      :class="[props.size === 'large' && 'text-base px-3 py-1']"
    >
      {{ props.content }}
    </Badge>
  </div>
</template>
